import {AfterViewInit, Component, Inject, OnInit, ViewChild} from '@angular/core';
import {
  BrandSkippedStripeProduct,
  BrandVendorProductResolution,
  BrandResolvedVendorProduct,
  VendorProductsService,
} from '../../../services/vendorproducts/vendorproducts.service';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {SelectionModel} from '@angular/cdk/collections';
import {MatSort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {TeamCompaniesService} from '../../../services/teamcompanies/teamcompanies.service';
import {
  BenefitCategoryTab as PayrollBenefitCategory,
  getBenefitCategoryLabel,
  getPayrollBenefitSubcategoryDefinitionsByCategory,
  getPayrollBenefitSubcategoryLabel,
  getPayrollBenefitSubcategoryOptions,
  normalizePayrollBenefitMapping,
  normalizePayrollBenefitSubcategoryDefinitionsByCategory,
  normalizeProductPayrollBenefitMap,
  PAYROLL_BENEFIT_CATEGORY_OPTIONS,
  PayrollBenefitSubcategoryDefinitionsByCategory,
  PayrollBenefitSubcategoryDefinition,
  ProductBenefitMapping,
} from '../../../services/payroll-benefits-assignment/payroll-benefit-categories';

export type CompanyProductSettingMode = 'inherit' | 'none' | 'custom';

export interface CompanyProductsDialogResult {
  mode: CompanyProductSettingMode;
  products: string[];
  alwaysActiveProducts?: string[];
  requiresApprovalProducts?: string[];
  productPayrollBenefitCategoriesMap?: Record<string, ProductBenefitMapping>;
  payrollBenefitSubcategoryDefinitionsByCategory?: PayrollBenefitSubcategoryDefinitionsByCategory;
  payrollDeductionDisabledProducts?: string[];
  allowanceControlDisabledProducts?: string[];
}

export interface CompanyProductsBrandOption {
  id: string;
  label: string;
  slug?: string;
  subtitle?: string;
}

export interface CompanyProductsCompanyOption {
  id: string;
  label: string;
  subtitle?: string;
}

export type CompanyProductImportSourceType = 'brand' | 'company';
export type CompanyProductImportApplyMode = 'replace' | 'merge';

@Component({
  selector: 'app-companyproductsdialog',
  templateUrl: './companyproductsdialog.component.html',
  styleUrls: ['./companyproductsdialog.component.scss']
})
export class CompanyProductsDialogComponent implements OnInit, AfterViewInit {

  // @ts-ignore
  @ViewChild(MatSort) sort: MatSort;

  public selection = new SelectionModel<any>(true, []);
  public alwaysActiveSelection = new SelectionModel<any>(true, []);
  public requiresApprovalSelection = new SelectionModel<any>(true, []);
  public payrollDeductionSelection = new SelectionModel<any>(true, []);
  public allowanceControlSelection = new SelectionModel<any>(true, []);
  public items = new MatTableDataSource<any>();
  public readonly productGroupHeaderColumns = ['generalProductsGroup', 'payrollAdminGroup'];
  public allColumnLabels: string[] = [];
  public mode: CompanyProductSettingMode;
  private products;
  private companyId;
  private effectiveProductIds: string[];
  private inheritedProductIds: string[];
  private customProductIds: string[];
  private inheritedAlwaysActiveProductIds: string[];
  private customAlwaysActiveProductIds: string[];
  private effectiveProductPayrollBenefitCategoriesMap: Record<string, ProductBenefitMapping>;
  private customProductPayrollBenefitCategoriesMap: Record<string, ProductBenefitMapping>;
  private payrollBenefitSubcategoryDefinitionsByCategory: Record<PayrollBenefitCategory, PayrollBenefitSubcategoryDefinition[]>;
  private customPayrollBenefitSubcategoryDefinitionsByCategory: PayrollBenefitSubcategoryDefinitionsByCategory;
  private customRequiresApprovalProductIds: string[];
  private inheritedPayrollDeductionDisabledProductIds: string[];
  private customPayrollDeductionDisabledProductIds: string[];
  private inheritedAllowanceControlDisabledProductIds: string[];
  private customAllowanceControlDisabledProductIds: string[];
  public company: any;
  public selectedBrandProductBrandId = '';
  public selectedCompanyProductCompanyId = '';
  public productImportSourceType: CompanyProductImportSourceType;
  public productImportApplyMode: CompanyProductImportApplyMode = 'replace';
  public productImportSourceLabel = '';
  public productImportPreviewLoaded = false;
  public companyProductPreviewUnavailableProductIds: string[] = [];
  public companyProductPreviewAlwaysActiveProductIds: string[] = [];
  public companyProductPreviewProductPayrollBenefitCategoriesMap: Record<string, ProductBenefitMapping> = {};
  public companyProductPreviewPayrollDeductionDisabledProductIds: string[] = [];
  public companyProductPreviewAllowanceControlDisabledProductIds: string[] = [];
  private companyProductPreviewSubcategoryDefinitionsByCategory: PayrollBenefitSubcategoryDefinitionsByCategory = {};
  private companyProductSubcategoryDefinitionsApplied = false;
  public brandProductResolution?: BrandVendorProductResolution;
  public brandProductPreviewProductIds: string[] = [];
  public brandProductPreviewAddedProductIds: string[] = [];
  public brandProductPreviewOverlappingProductIds: string[] = [];
  public brandProductPreviewRemovedProductIds: string[] = [];
  public brandProductPreviewMatchedProducts: BrandResolvedVendorProduct[] = [];
  public brandProductPreviewSkippedProducts: BrandSkippedStripeProduct[] = [];
  public brandProductResolutionError = '';
  public brandProductResolutionApplied = false;
  public isBrandProductResolutionLoading = false;
  public isBrandProductHelperExpanded = false;
  public showSelectedProductsOnly = false;
  public productSearch = '';
  public readonly productColumnHelpText = {
    select: 'Select products to include in this company\'s Available Products list. Unselected products are hidden from this company and cannot be configured by the dependent options.',
    alwaysActive: 'Selected products checked here are forced into each team\'s effective product selection, even when the company or team active-products list would otherwise omit them.',
    payrollBenefitCategory: 'Assign a payroll benefit category to selected products here. This also enables the Requires Approval option.',
    payrollBenefitSubcategory: 'Optional section within the selected payroll benefit category.',
    requiresApproval: 'Assign a payroll benefit category to enable this option. Checked products require Wallit onboarding approval before they can be enabled for teams and employees.',
    payrollDeduction: 'Checked products remain eligible for payroll-deduction setup and deduction-code mapping. Unchecked selected products are saved as payroll deduction disabled.',
    allowanceControl: 'Allow company admins to choose whether this product can be paid with Wallit allowance.',
  };
  public readonly payrollBenefitCategoryOptions = PAYROLL_BENEFIT_CATEGORY_OPTIONS;

  constructor(
      private vendorProductsService: VendorProductsService,
      private teamCompaniesService: TeamCompaniesService,
      public dialogRef: MatDialogRef<CompanyProductsDialogComponent>,
      @Inject(MAT_DIALOG_DATA) public data: CompanyProductsDialogModel
  ) {
    this.products = data.products;
    this.companyId = data.companyId;
    this.mode = data.initialMode;
    this.allColumnLabels = [
      'select',
      'vendorName',
      'productName',
      'vendorSlug',
      'payrollBenefitCategory',
      'payrollBenefitSubcategory',
      'requiresApproval',
      ...(data.showPayrollDeductionColumn ? ['payrollDeduction'] : []),
      ...(data.showAllowanceControlColumn ? ['allowanceControl'] : []),
      'alwaysActive',
    ];
    this.effectiveProductIds = Array.isArray(data.products) ? [...data.products] : [];
    this.inheritedProductIds = Array.isArray(data.inheritedProducts) ? [...data.inheritedProducts] : [];
    this.customProductIds = data.initialMode === 'none'
      ? []
      : [...this.effectiveProductIds];
    this.inheritedAlwaysActiveProductIds = Array.isArray(data.inheritedAlwaysActiveProducts)
      ? [...data.inheritedAlwaysActiveProducts]
      : [];
    this.effectiveProductPayrollBenefitCategoriesMap = normalizeProductPayrollBenefitMap(
      data.productPayrollBenefitCategoriesMap || {}
    );
    this.customProductPayrollBenefitCategoriesMap = data.initialMode === 'none'
      ? {}
      : this.cloneProductPayrollBenefitCategoriesMap(this.effectiveProductPayrollBenefitCategoriesMap);
    this.customPayrollBenefitSubcategoryDefinitionsByCategory =
      normalizePayrollBenefitSubcategoryDefinitionsByCategory(
        data.payrollBenefitSubcategoryDefinitionsByCategory || {}
      );
    this.payrollBenefitSubcategoryDefinitionsByCategory = getPayrollBenefitSubcategoryDefinitionsByCategory(
      this.customPayrollBenefitSubcategoryDefinitionsByCategory
    );
    this.customAlwaysActiveProductIds = this.pruneProductIdsToAvailableProducts(
      Array.isArray(data.alwaysActiveProducts) ? data.alwaysActiveProducts : [],
      this.getSelectedIdsForMode()
    );
    this.customRequiresApprovalProductIds = this.pruneProductIdsToAvailableProducts(
      this.getRequiresApprovalProductIds(this.effectiveProductPayrollBenefitCategoriesMap),
      this.getEligibleRequiresApprovalProductIds(this.getSelectedIdsForMode())
    );
    this.inheritedPayrollDeductionDisabledProductIds = this.pruneProductIdsToAvailableProducts(
      Array.isArray(data.inheritedPayrollDeductionDisabledProducts)
        ? data.inheritedPayrollDeductionDisabledProducts
        : [],
      this.getSelectedIdsForMode()
    );
    this.customPayrollDeductionDisabledProductIds = this.pruneProductIdsToAvailableProducts(
      Array.isArray(data.payrollDeductionDisabledProducts) ? data.payrollDeductionDisabledProducts : [],
      this.getSelectedIdsForMode()
    );
    this.inheritedAllowanceControlDisabledProductIds = this.pruneProductIdsToAvailableProducts(
      Array.isArray(data.inheritedAllowanceControlDisabledProducts)
        ? data.inheritedAllowanceControlDisabledProducts
        : [],
      this.getSelectedIdsForMode()
    );
    this.customAllowanceControlDisabledProductIds = this.pruneProductIdsToAvailableProducts(
      Array.isArray(data.allowanceControlDisabledProducts) ? data.allowanceControlDisabledProducts : [],
      this.getSelectedIdsForMode()
    );
    this.selectedBrandProductBrandId = data.initialBrandId || '';
    this.productImportSourceType = data.brandOptions.length > 0 ? 'brand' : 'company';
    this.items.filterPredicate = (product, filter) => this.productMatchesFilter(product, filter);
  }

  ngOnInit(
  ): void {
    this.vendorProductsService.getAllVendorProducts().then(products => {
      this.items.sort = this.sort;
      const setProducts = () => {
        this.items.data = products;
        this.syncSelectionToMode();
        this.syncAlwaysActiveSelectionToMode();
        this.syncRequiresApprovalSelectionToMode();
        this.syncPayrollDeductionSelectionToMode();
        this.syncAllowanceControlSelectionToMode();
      };
      if (this.companyId) {
        this.teamCompaniesService.getCompany(this.companyId).then(company => {
          this.company = company;
          const availableProductIds = this.teamCompaniesService.getAvailableProductIds(company);
          products = products.filter((product: { id: string; }) => availableProductIds.find((productId: string) => productId === product.id));
          setProducts();
        });
      } else {
        setProducts();
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.sort) {
      this.items.sort = this.sort;
      this.sort.active = 'vendorName';
      this.sort.direction = 'asc';
    }
  }

  okClicked(): void {
    this.captureCustomSelectionFromTable();
    this.captureCustomAlwaysActiveSelectionFromTable();
    this.captureCustomRequiresApprovalSelectionFromTable();
    this.captureCustomPayrollDeductionSelectionFromTable();
    this.captureCustomAllowanceControlSelectionFromTable();
    const products = this.mode === 'custom' ? [...this.customProductIds] : [];
    const alwaysActiveProducts = this.mode === 'custom'
      ? this.pruneProductIdsToAvailableProducts(this.customAlwaysActiveProductIds, products)
      : [];
    const requiresApprovalProducts = this.mode === 'custom'
      ? this.pruneProductIdsToAvailableProducts(
        this.customRequiresApprovalProductIds,
        this.getEligibleRequiresApprovalProductIds(products)
      )
      : [];
    const productPayrollBenefitCategoriesMap = this.mode === 'custom'
      ? this.buildCustomProductPayrollBenefitCategoriesMap(products, requiresApprovalProducts)
      : {};
    const payrollDeductionDisabledProducts = this.mode === 'custom'
      ? this.pruneProductIdsToAvailableProducts(this.customPayrollDeductionDisabledProductIds, products)
      : [];
    const allowanceControlDisabledProducts = this.mode === 'custom'
      ? this.pruneProductIdsToAvailableProducts(this.customAllowanceControlDisabledProductIds, products)
      : [];
    const result: CompanyProductsDialogResult = {
      mode: this.mode,
      products,
      alwaysActiveProducts,
      requiresApprovalProducts,
      productPayrollBenefitCategoriesMap,
      payrollDeductionDisabledProducts,
      allowanceControlDisabledProducts,
    };
    if (this.companyProductSubcategoryDefinitionsApplied) {
      result.payrollBenefitSubcategoryDefinitionsByCategory =
        this.clonePayrollBenefitSubcategoryDefinitionsByCategory(
          this.customPayrollBenefitSubcategoryDefinitionsByCategory
        );
    }
    this.dialogRef.close(result);
  }

  onDismiss(): void {
    this.dialogRef.close(undefined);
  }

  canInherit(): boolean {
    return !!this.data.canInherit;
  }

  setMode(mode: CompanyProductSettingMode): void {
    if (mode === this.mode) {
      return;
    }

    this.captureCustomSelectionFromTable();
    this.captureCustomAlwaysActiveSelectionFromTable();
    this.captureCustomRequiresApprovalSelectionFromTable();
    this.captureCustomPayrollDeductionSelectionFromTable();
    this.captureCustomAllowanceControlSelectionFromTable();
    const selectedIdsForTargetMode = this.getSelectedIdsForMode();
    const alwaysActiveIdsForTargetMode = this.getSelectedAlwaysActiveIdsForMode();
    const requiresApprovalIdsForTargetMode = this.getSelectedRequiresApprovalIdsForMode();
    const payrollDeductionDisabledIdsForTargetMode = this.getPayrollDeductionDisabledProductIdsForMode();
    const allowanceControlDisabledIdsForTargetMode = this.getAllowanceControlDisabledProductIdsForMode();

    if (mode === 'custom' && this.mode !== 'custom') {
      this.customProductIds = [...selectedIdsForTargetMode];
    }

    this.mode = mode;
    this.syncSelectionToMode();
    this.customAlwaysActiveProductIds = mode === 'custom'
      ? this.pruneProductIdsToAvailableProducts(alwaysActiveIdsForTargetMode, this.getSelectedIdsForMode())
      : [];
    this.syncAlwaysActiveSelectionToMode();
    this.customRequiresApprovalProductIds = mode === 'custom'
      ? this.pruneProductIdsToAvailableProducts(
        requiresApprovalIdsForTargetMode,
        this.getEligibleRequiresApprovalProductIds(this.getSelectedIdsForMode())
      )
      : [];
    this.syncRequiresApprovalSelectionToMode();
    if (mode === 'custom') {
      this.pruneCustomProductPayrollBenefitCategoriesMapToSelectedProducts();
    }
    this.customPayrollDeductionDisabledProductIds = mode === 'custom'
      ? this.pruneProductIdsToAvailableProducts(
        payrollDeductionDisabledIdsForTargetMode,
        this.getSelectedIdsForMode()
      )
      : [];
    this.syncPayrollDeductionSelectionToMode();
    this.customAllowanceControlDisabledProductIds = mode === 'custom'
      ? this.pruneProductIdsToAvailableProducts(
        allowanceControlDisabledIdsForTargetMode,
        this.getSelectedIdsForMode()
      )
      : [];
    this.syncAllowanceControlSelectionToMode();
  }

  isCustomMode(): boolean {
    return this.mode === 'custom';
  }

  isAllSelected(): boolean {
    if (!this.isCustomMode()) {
      return false;
    }
    const numSelected = this.selection.selected.length;
    const numRows = this.items.data.length;
    return numSelected === numRows;
  }

  isSomeSelected(): boolean {
    if (!this.isCustomMode()) {
      return false;
    }
    return this.selection.selected.length > 0;
  }

  isAlwaysActiveProductAvailable(productId: string): boolean {
    return this.getSelectedIdsForMode().includes(productId);
  }

  isRequiresApprovalProductAvailable(productId: string): boolean {
    return this.getSelectedIdsForMode().includes(productId)
      && !!this.getProductPayrollBenefitMapping(productId)?.category;
  }

  isPayrollDeductionProductAvailable(productId: string): boolean {
    return this.getSelectedIdsForMode().includes(productId);
  }

  isAllowanceControlProductAvailable(productId: string): boolean {
    return this.getSelectedIdsForMode().includes(productId);
  }

  canEditProductPayrollBenefitCategory(productId: string): boolean {
    return this.isCustomMode() && this.getSelectedIdsForMode().includes(productId);
  }

  getProductPayrollBenefitMapping(productId: string): ProductBenefitMapping | null {
    if (this.mode === 'inherit') {
      return this.effectiveProductPayrollBenefitCategoriesMap[productId] || null;
    }

    if (this.mode === 'none') {
      return null;
    }

    return this.customProductPayrollBenefitCategoriesMap[productId] || null;
  }

  getProductPayrollBenefitCategory(productId: string): PayrollBenefitCategory | '' {
    return this.getProductPayrollBenefitMapping(productId)?.category || '';
  }

  getProductPayrollBenefitCategoryLabel(productId: string): string {
    const category = this.getProductPayrollBenefitCategory(productId);
    return category ? getBenefitCategoryLabel(category) : '';
  }

  getProductPayrollBenefitSubcategory(productId: string): string {
    return this.getProductPayrollBenefitMapping(productId)?.subcategory || '';
  }

  getProductPayrollBenefitSubcategoryLabel(productId: string): string {
    const mapping = this.getProductPayrollBenefitMapping(productId);
    return getPayrollBenefitSubcategoryLabel(
      mapping?.category || '',
      mapping?.subcategory || '',
      this.payrollBenefitSubcategoryDefinitionsByCategory,
    );
  }

  getPayrollBenefitCategoryLabel(category: PayrollBenefitCategory): string {
    return getBenefitCategoryLabel(category);
  }

  getPayrollBenefitSubcategoryOptions(category: PayrollBenefitCategory | ''): string[] {
    return category
      ? getPayrollBenefitSubcategoryOptions(category, this.payrollBenefitSubcategoryDefinitionsByCategory)
      : [];
  }

  hasPayrollBenefitSubcategoryOptions(category: PayrollBenefitCategory | ''): boolean {
    return this.getPayrollBenefitSubcategoryOptions(category).length > 0;
  }

  getPayrollBenefitSubcategoryLabel(category: PayrollBenefitCategory | '', subcategory: string): string {
    return getPayrollBenefitSubcategoryLabel(
      category,
      subcategory,
      this.payrollBenefitSubcategoryDefinitionsByCategory,
    );
  }

  setProductPayrollBenefitCategory(productId: string, category: PayrollBenefitCategory | ''): void {
    if (!this.canEditProductPayrollBenefitCategory(productId)) {
      return;
    }

    const current = this.customProductPayrollBenefitCategoriesMap[productId];
    if (!category) {
      const nextMap = { ...this.customProductPayrollBenefitCategoriesMap };
      delete nextMap[productId];
      this.customProductPayrollBenefitCategoriesMap = nextMap;
      this.customRequiresApprovalProductIds = this.customRequiresApprovalProductIds.filter(
        (selectedProductId) => selectedProductId !== productId
      );
      this.syncRequiresApprovalSelectionToMode();
      return;
    }

    const nextMapping: ProductBenefitMapping = { category };
    const subcategoryOptions = this.getPayrollBenefitSubcategoryOptions(category);
    if (current?.subcategory && subcategoryOptions.includes(current.subcategory)) {
      nextMapping.subcategory = current.subcategory;
    }
    if (current?.requiresApproval === true || this.customRequiresApprovalProductIds.includes(productId)) {
      nextMapping.requiresApproval = true;
    }

    this.customProductPayrollBenefitCategoriesMap = {
      ...this.customProductPayrollBenefitCategoriesMap,
      [productId]: nextMapping,
    };
    this.syncRequiresApprovalSelectionToMode();
  }

  setProductPayrollBenefitSubcategory(productId: string, subcategory: string | ''): void {
    if (!this.canEditProductPayrollBenefitCategory(productId)) {
      return;
    }

    const current = this.customProductPayrollBenefitCategoriesMap[productId];
    const category = current?.category;
    if (!category) {
      return;
    }

    const nextMapping: ProductBenefitMapping = { category };
    const subcategoryOptions = this.getPayrollBenefitSubcategoryOptions(category);
    if (subcategory && subcategoryOptions.includes(subcategory)) {
      nextMapping.subcategory = subcategory;
    }
    if (current?.requiresApproval === true || this.customRequiresApprovalProductIds.includes(productId)) {
      nextMapping.requiresApproval = true;
    }

    this.customProductPayrollBenefitCategoriesMap = {
      ...this.customProductPayrollBenefitCategoriesMap,
      [productId]: nextMapping,
    };
  }

  toggleAlwaysActiveRow(row: any): void {
    if (!this.isCustomMode() || !this.isAlwaysActiveProductAvailable(row?.id)) {
      return;
    }

    this.alwaysActiveSelection.toggle(row);
    this.captureCustomAlwaysActiveSelectionFromTable();
  }

  toggleRequiresApprovalRow(row: any): void {
    if (!this.isCustomMode() || !this.isRequiresApprovalProductAvailable(row?.id)) {
      return;
    }

    this.requiresApprovalSelection.toggle(row);
    this.captureCustomRequiresApprovalSelectionFromTable();
  }

  togglePayrollDeductionRow(row: any): void {
    if (!this.isCustomMode() || !this.isPayrollDeductionProductAvailable(row?.id)) {
      return;
    }

    this.payrollDeductionSelection.toggle(row);
    this.captureCustomPayrollDeductionSelectionFromTable();
  }

  toggleAllowanceControlRow(row: any): void {
    if (!this.isCustomMode() || !this.isAllowanceControlProductAvailable(row?.id)) {
      return;
    }

    this.allowanceControlSelection.toggle(row);
    this.captureCustomAllowanceControlSelectionFromTable();
  }

  /* Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle(ref: { checked: boolean; }): void {
    if (!this.isCustomMode()) {
      return;
    }
    const previouslySelectedIds = new Set(this.getSelectedIdsForMode());
    if (this.isSomeSelected()) {
      this.selection.clear();
      ref.checked = false;
    } else {
      ref.checked = true;
      this.items.data.forEach((row: any) => this.selection.select(row));
    }
    this.captureCustomSelectionFromTable();
    if (ref.checked) {
      const newlySelectedProductIds = this.getSelectedIdsForMode()
        .filter((productId) => !previouslySelectedIds.has(productId));
      this.customPayrollDeductionDisabledProductIds = this.pruneProductIdsToAvailableProducts(
        Array.from(new Set([...this.customPayrollDeductionDisabledProductIds, ...newlySelectedProductIds])),
        this.getSelectedIdsForMode()
      );
    }
    this.refreshProductTableFilter();
    this.customAlwaysActiveProductIds = this.pruneProductIdsToAvailableProducts(
      this.customAlwaysActiveProductIds,
      this.getSelectedIdsForMode()
    );
    this.syncAlwaysActiveSelectionToMode();
    this.customRequiresApprovalProductIds = this.pruneProductIdsToAvailableProducts(
      this.customRequiresApprovalProductIds,
      this.getEligibleRequiresApprovalProductIds(this.getSelectedIdsForMode())
    );
    this.syncRequiresApprovalSelectionToMode();
    this.customPayrollDeductionDisabledProductIds = this.pruneProductIdsToAvailableProducts(
      this.customPayrollDeductionDisabledProductIds,
      this.getSelectedIdsForMode()
    );
    this.syncPayrollDeductionSelectionToMode();
    this.customAllowanceControlDisabledProductIds = this.pruneProductIdsToAvailableProducts(
      this.customAllowanceControlDisabledProductIds,
      this.getSelectedIdsForMode()
    );
    this.syncAllowanceControlSelectionToMode();
    this.pruneCustomProductPayrollBenefitCategoriesMapToSelectedProducts();
  }

  toggleRow(row: any): void {
    if (!this.isCustomMode()) {
      return;
    }

    const wasSelected = this.selection.isSelected(row);
    this.selection.toggle(row);
    this.captureCustomSelectionFromTable();
    if (!wasSelected && row?.id) {
      this.customPayrollDeductionDisabledProductIds = this.pruneProductIdsToAvailableProducts(
        Array.from(new Set([...this.customPayrollDeductionDisabledProductIds, row.id])),
        this.getSelectedIdsForMode()
      );
    }
    this.refreshProductTableFilter();
    this.customAlwaysActiveProductIds = this.pruneProductIdsToAvailableProducts(
      this.customAlwaysActiveProductIds,
      this.getSelectedIdsForMode()
    );
    this.syncAlwaysActiveSelectionToMode();
    this.customRequiresApprovalProductIds = this.pruneProductIdsToAvailableProducts(
      this.customRequiresApprovalProductIds,
      this.getEligibleRequiresApprovalProductIds(this.getSelectedIdsForMode())
    );
    this.syncRequiresApprovalSelectionToMode();
    this.customPayrollDeductionDisabledProductIds = this.pruneProductIdsToAvailableProducts(
      this.customPayrollDeductionDisabledProductIds,
      this.getSelectedIdsForMode()
    );
    this.syncPayrollDeductionSelectionToMode();
    this.customAllowanceControlDisabledProductIds = this.pruneProductIdsToAvailableProducts(
      this.customAllowanceControlDisabledProductIds,
      this.getSelectedIdsForMode()
    );
    this.syncAllowanceControlSelectionToMode();
    this.pruneCustomProductPayrollBenefitCategoriesMapToSelectedProducts();
  }

  setShowSelectedProductsOnly(showSelectedProductsOnly: boolean): void {
    this.showSelectedProductsOnly = showSelectedProductsOnly;
    this.refreshProductTableFilter();
  }

  setProductSearch(productSearch: string): void {
    this.productSearch = productSearch || '';
    this.refreshProductTableFilter();
  }

  clearProductSearch(): void {
    this.setProductSearch('');
  }

  getSelectedProductCount(): number {
    return this.selection.selected.length;
  }

  getPayrollAdminColumnCount(): number {
    return 4
      + (this.data.showPayrollDeductionColumn ? 1 : 0)
      + (this.data.showAllowanceControlColumn ? 1 : 0);
  }

  toggleBrandProductHelper(): void {
    this.isBrandProductHelperExpanded = !this.isBrandProductHelperExpanded;
  }

  hasProductImportOptions(): boolean {
    return (this.data.brandOptions || []).length > 0 || (this.data.companyOptions || []).length > 0;
  }

  onProductImportSourceTypeChange(sourceType: CompanyProductImportSourceType): void {
    this.productImportSourceType = sourceType;
    this.resetProductImportPreview();
  }

  setProductImportApplyMode(applyMode: CompanyProductImportApplyMode): void {
    this.productImportApplyMode = applyMode;
    if (this.productImportPreviewLoaded) {
      this.buildProductImportDiff();
    }
  }

  getSelectedBrandProductBrandLabel(): string {
    const selectedBrand = (this.data.brandOptions || [])
      .find((brand) => brand.id === this.selectedBrandProductBrandId);
    return selectedBrand?.label || selectedBrand?.slug || this.selectedBrandProductBrandId || '';
  }

  async loadBrandProductPreview(): Promise<void> {
    this.captureCustomSelectionFromTable();
    this.captureCustomAlwaysActiveSelectionFromTable();
    this.captureCustomRequiresApprovalSelectionFromTable();
    this.captureCustomPayrollDeductionSelectionFromTable();
    this.resetProductImportPreview();

    if (!this.selectedBrandProductBrandId) {
      this.brandProductResolutionError = 'Choose a brand first.';
      return;
    }

    this.isBrandProductResolutionLoading = true;
    try {
      const resolution = await this.vendorProductsService.getBrandAvailableVendorProducts(this.selectedBrandProductBrandId);
      this.brandProductResolution = resolution;
      this.productImportSourceLabel = resolution?.brand?.name
        || resolution?.brand?.slug
        || this.getSelectedBrandProductBrandLabel()
        || 'selected brand';
      this.buildBrandProductPreview(resolution);
      this.productImportPreviewLoaded = true;
    } catch (error: any) {
      this.brandProductResolutionError = error?.error?.error || error?.message || 'Unable to load brand products.';
    } finally {
      this.isBrandProductResolutionLoading = false;
    }
  }

  async loadCompanyProductPreview(): Promise<void> {
    this.captureProductImportSelections();
    this.resetProductImportPreview();

    if (!this.selectedCompanyProductCompanyId) {
      this.brandProductResolutionError = 'Choose a company first.';
      return;
    }

    this.isBrandProductResolutionLoading = true;
    try {
      const company = await this.teamCompaniesService.getCompany(this.selectedCompanyProductCompanyId);
      const resolvedPayrollConfig = this.teamCompaniesService.getResolvedPayrollConfig(company);
      const sourceProductIds = this.teamCompaniesService.getAvailableProductIds(company);
      const selectableProductIds = new Set((this.items.data || []).map((product: any) => product.id));
      const uniqueSourceProductIds = Array.from(new Set(sourceProductIds || []));
      this.brandProductPreviewProductIds = uniqueSourceProductIds
        .filter((productId) => selectableProductIds.has(productId));
      this.companyProductPreviewUnavailableProductIds = uniqueSourceProductIds
        .filter((productId) => !selectableProductIds.has(productId));
      const sourceProductIdSet = new Set(this.brandProductPreviewProductIds);
      this.companyProductPreviewAlwaysActiveProductIds = this.teamCompaniesService
        .getAlwaysActiveProductIds(company)
        .filter((productId) => sourceProductIdSet.has(productId));
      this.companyProductPreviewProductPayrollBenefitCategoriesMap = Object.entries(
        normalizeProductPayrollBenefitMap(resolvedPayrollConfig?.productPayrollBenefitCategoriesMap || {})
      ).reduce((result: Record<string, ProductBenefitMapping>, [productId, mapping]) => {
        if (sourceProductIdSet.has(productId)) {
          result[productId] = mapping;
        }
        return result;
      }, {});
      this.companyProductPreviewPayrollDeductionDisabledProductIds = this.teamCompaniesService
        .getPayrollDeductionDisabledProductIds(company)
        .filter((productId) => sourceProductIdSet.has(productId));
      this.companyProductPreviewAllowanceControlDisabledProductIds = this.teamCompaniesService
        .getAllowanceControlDisabledProductIds(company)
        .filter((productId) => sourceProductIdSet.has(productId));
      this.companyProductPreviewSubcategoryDefinitionsByCategory =
        normalizePayrollBenefitSubcategoryDefinitionsByCategory(
          resolvedPayrollConfig?.payrollBenefitSubcategoryDefinitionsByCategory || {}
        );
      const option = (this.data.companyOptions || [])
        .find((companyOption) => companyOption.id === this.selectedCompanyProductCompanyId);
      this.productImportSourceLabel = company?.name || option?.label || 'selected company';
      this.productImportPreviewLoaded = true;
      this.buildProductImportDiff();
    } catch (error: any) {
      this.brandProductResolutionError = error?.error?.error || error?.message || 'Unable to load company products.';
    } finally {
      this.isBrandProductResolutionLoading = false;
    }
  }

  loadProductImportPreview(): Promise<void> {
    return this.productImportSourceType === 'company'
      ? this.loadCompanyProductPreview()
      : this.loadBrandProductPreview();
  }

  canApplyBrandProductPreview(): boolean {
    return this.brandProductPreviewProductIds.length > 0 && !this.isBrandProductResolutionLoading;
  }

  applyBrandProductPreview(): void {
    if (!this.canApplyBrandProductPreview()) {
      return;
    }

    const currentAlwaysActiveProductIds = this.getSelectedAlwaysActiveIdsForMode();
    const currentRequiresApprovalProductIds = this.getSelectedRequiresApprovalIdsForMode();
    const currentPayrollDeductionDisabledProductIds = this.getPayrollDeductionDisabledProductIdsForMode();
    const currentAllowanceControlDisabledProductIds = this.getAllowanceControlDisabledProductIdsForMode();
    const currentProductIds = new Set(this.getSelectedIdsForMode());
    const nextProductIds = this.productImportApplyMode === 'merge'
      ? Array.from(new Set([...currentProductIds, ...this.brandProductPreviewProductIds]))
      : [...this.brandProductPreviewProductIds];
    const newlySelectedProductIds = nextProductIds
      .filter((productId) => !currentProductIds.has(productId));

    this.mode = 'custom';
    this.customProductIds = nextProductIds;
    if (this.productImportSourceType === 'company') {
      const sourceProductIdSet = new Set(this.brandProductPreviewProductIds);
      this.customAlwaysActiveProductIds = this.mergeCompanyProductIdSetting(
        currentAlwaysActiveProductIds,
        this.companyProductPreviewAlwaysActiveProductIds,
        sourceProductIdSet,
        nextProductIds,
      );
      this.customProductPayrollBenefitCategoriesMap = this.mergeCompanyProductBenefitMappings(
        this.customProductPayrollBenefitCategoriesMap,
        this.companyProductPreviewProductPayrollBenefitCategoriesMap,
        sourceProductIdSet,
        nextProductIds,
      );
      this.customRequiresApprovalProductIds = this.getRequiresApprovalProductIds(
        this.customProductPayrollBenefitCategoriesMap
      );
      this.customPayrollDeductionDisabledProductIds = this.mergeCompanyProductIdSetting(
        currentPayrollDeductionDisabledProductIds,
        this.companyProductPreviewPayrollDeductionDisabledProductIds,
        sourceProductIdSet,
        nextProductIds,
      );
      this.customAllowanceControlDisabledProductIds = this.mergeCompanyProductIdSetting(
        currentAllowanceControlDisabledProductIds,
        this.companyProductPreviewAllowanceControlDisabledProductIds,
        sourceProductIdSet,
        nextProductIds,
      );
      this.customPayrollBenefitSubcategoryDefinitionsByCategory = this.mergePayrollBenefitSubcategoryDefinitions(
        this.customPayrollBenefitSubcategoryDefinitionsByCategory,
        this.companyProductPreviewSubcategoryDefinitionsByCategory,
      );
      this.payrollBenefitSubcategoryDefinitionsByCategory = getPayrollBenefitSubcategoryDefinitionsByCategory(
        this.customPayrollBenefitSubcategoryDefinitionsByCategory
      );
      this.companyProductSubcategoryDefinitionsApplied = this.companyProductSubcategoryDefinitionsApplied
        || Object.keys(this.companyProductPreviewSubcategoryDefinitionsByCategory).length > 0;
    } else {
      this.customAlwaysActiveProductIds = this.pruneProductIdsToAvailableProducts(
        currentAlwaysActiveProductIds,
        nextProductIds
      );
      this.customRequiresApprovalProductIds = this.pruneProductIdsToAvailableProducts(
        currentRequiresApprovalProductIds,
        this.getEligibleRequiresApprovalProductIds(nextProductIds)
      );
      this.customPayrollDeductionDisabledProductIds = this.pruneProductIdsToAvailableProducts(
        Array.from(new Set([...currentPayrollDeductionDisabledProductIds, ...newlySelectedProductIds])),
        nextProductIds
      );
      this.customAllowanceControlDisabledProductIds = this.pruneProductIdsToAvailableProducts(
        currentAllowanceControlDisabledProductIds,
        nextProductIds
      );
    }
    this.syncSelectionToMode();
    this.syncAlwaysActiveSelectionToMode();
    this.syncRequiresApprovalSelectionToMode();
    this.syncPayrollDeductionSelectionToMode();
    this.syncAllowanceControlSelectionToMode();
    this.pruneCustomProductPayrollBenefitCategoriesMapToSelectedProducts();
    this.brandProductResolutionApplied = true;
  }

  hasBrandProductNoMatchState(): boolean {
    return this.productImportPreviewLoaded
      && this.brandProductPreviewProductIds.length === 0
      && !this.isBrandProductResolutionLoading;
  }

  hasBrandProductPartialMatchState(): boolean {
    return this.brandProductPreviewProductIds.length > 0
      && this.brandProductPreviewSkippedProducts.length > 0;
  }

  getProductDisplay(productId: string): string {
    const product = (this.items.data || []).find((item: any) => item.id === productId);
    if (!product) {
      return productId;
    }

    return [product.vendorName, product.productName]
      .filter(value => !!value)
      .join(' - ') || productId;
  }

  getBrandProductDisplay(product: BrandResolvedVendorProduct): string {
    return [product?.vendorName, product?.productName]
      .filter(value => !!value)
      .join(' - ') || product?.id || '';
  }

  getSkippedStripeProductDisplay(product: BrandSkippedStripeProduct): string {
    return [product?.name, product?.id]
      .filter(value => !!value)
      .join(' - ');
  }

  getSkippedStripeProductReason(product: BrandSkippedStripeProduct): string {
    switch (product?.reason) {
      case 'no_vendor_product':
        return 'No vendor product mapping';
      case 'duplicate_vendor_product_mapping':
        return 'Multiple vendor products use this brand product';
      case 'inactive_stripe_product':
        return 'Inactive brand product';
      case 'missing_stripe_product_id':
      case 'missing_vendor_product_id':
        return 'Missing vendor product ID';
      default:
        return product?.reason || 'Skipped';
    }
  }

  private buildBrandProductPreview(resolution: BrandVendorProductResolution): void {
    const matchedProducts = Array.isArray(resolution?.matchedVendorProducts)
      ? resolution.matchedVendorProducts
      : [];
    const seenProductIds = new Set<string>();
    this.brandProductPreviewMatchedProducts = matchedProducts.filter((product) => {
      const productId = `${product?.id || ''}`.trim();
      if (!productId || seenProductIds.has(productId)) {
        return false;
      }
      seenProductIds.add(productId);
      return true;
    });
    this.brandProductPreviewProductIds = this.brandProductPreviewMatchedProducts.map((product) => product.id);
    const skippedProducts = Array.isArray(resolution?.skippedSourceProducts)
      ? resolution.skippedSourceProducts
      : resolution?.skippedStripeProducts;
    this.brandProductPreviewSkippedProducts = Array.isArray(skippedProducts)
      ? skippedProducts
      : [];

    this.buildProductImportDiff();
  }

  private captureProductImportSelections(): void {
    this.captureCustomSelectionFromTable();
    this.captureCustomAlwaysActiveSelectionFromTable();
    this.captureCustomRequiresApprovalSelectionFromTable();
    this.captureCustomPayrollDeductionSelectionFromTable();
    this.captureCustomAllowanceControlSelectionFromTable();
  }

  private resetProductImportPreview(): void {
    this.brandProductResolutionError = '';
    this.brandProductResolutionApplied = false;
    this.brandProductResolution = undefined;
    this.brandProductPreviewProductIds = [];
    this.brandProductPreviewAddedProductIds = [];
    this.brandProductPreviewOverlappingProductIds = [];
    this.brandProductPreviewRemovedProductIds = [];
    this.brandProductPreviewMatchedProducts = [];
    this.brandProductPreviewSkippedProducts = [];
    this.companyProductPreviewUnavailableProductIds = [];
    this.companyProductPreviewAlwaysActiveProductIds = [];
    this.companyProductPreviewProductPayrollBenefitCategoriesMap = {};
    this.companyProductPreviewPayrollDeductionDisabledProductIds = [];
    this.companyProductPreviewAllowanceControlDisabledProductIds = [];
    this.companyProductPreviewSubcategoryDefinitionsByCategory = {};
    this.productImportSourceLabel = '';
    this.productImportPreviewLoaded = false;
  }

  private buildProductImportDiff(): void {
    const currentProductIds = new Set(this.getSelectedIdsForMode());
    const sourceProductIds = new Set(this.brandProductPreviewProductIds);
    this.brandProductPreviewAddedProductIds = this.brandProductPreviewProductIds
      .filter((productId) => !currentProductIds.has(productId));
    this.brandProductPreviewOverlappingProductIds = this.brandProductPreviewProductIds
      .filter((productId) => currentProductIds.has(productId));
    this.brandProductPreviewRemovedProductIds = this.productImportApplyMode === 'replace'
      ? Array.from(currentProductIds).filter((productId) => !sourceProductIds.has(productId))
      : [];
  }

  private mergeCompanyProductIdSetting(
    currentProductIds: string[],
    sourceProductIds: string[],
    importedProductIds: Set<string>,
    nextAvailableProductIds: string[],
  ): string[] {
    const preservedProductIds = this.productImportApplyMode === 'merge'
      ? (currentProductIds || []).filter((productId) => !importedProductIds.has(productId))
      : [];
    return this.pruneProductIdsToAvailableProducts(
      Array.from(new Set([...preservedProductIds, ...(sourceProductIds || [])])),
      nextAvailableProductIds,
    );
  }

  private mergeCompanyProductBenefitMappings(
    currentMappings: Record<string, ProductBenefitMapping>,
    sourceMappings: Record<string, ProductBenefitMapping>,
    importedProductIds: Set<string>,
    nextAvailableProductIds: string[],
  ): Record<string, ProductBenefitMapping> {
    const nextAvailableProductIdSet = new Set(nextAvailableProductIds || []);
    const preservedMappings = this.productImportApplyMode === 'merge'
      ? Object.entries(currentMappings || {}).reduce(
        (result: Record<string, ProductBenefitMapping>, [productId, mapping]) => {
          if (!importedProductIds.has(productId) && nextAvailableProductIdSet.has(productId)) {
            result[productId] = mapping;
          }
          return result;
        },
        {},
      )
      : {};

    return Object.entries(sourceMappings || {}).reduce(
      (result: Record<string, ProductBenefitMapping>, [productId, mapping]) => {
        if (nextAvailableProductIdSet.has(productId)) {
          result[productId] = mapping;
        }
        return result;
      },
      preservedMappings,
    );
  }

  private mergePayrollBenefitSubcategoryDefinitions(
    currentDefinitions: PayrollBenefitSubcategoryDefinitionsByCategory,
    sourceDefinitions: PayrollBenefitSubcategoryDefinitionsByCategory,
  ): PayrollBenefitSubcategoryDefinitionsByCategory {
    const mergedDefinitions = this.clonePayrollBenefitSubcategoryDefinitionsByCategory(currentDefinitions);
    Object.entries(sourceDefinitions || {}).forEach(([category, definitions]) => {
      const normalizedCategory = category as PayrollBenefitCategory;
      const definitionsById = new Map(
        (mergedDefinitions[normalizedCategory] || []).map((definition) => [definition.id, definition])
      );
      (definitions || []).forEach((definition) => definitionsById.set(definition.id, definition));
      mergedDefinitions[normalizedCategory] = Array.from(definitionsById.values());
    });
    return mergedDefinitions;
  }

  private syncSelectionToMode(): void {
    const selectedIds = new Set(this.getSelectedIdsForMode());
    this.selection = new SelectionModel<any>(true, []);
    this.items.data.forEach((item: any) => {
      if (selectedIds.has(item.id)) {
        this.selection.select(item);
      }
    });
    this.refreshProductTableFilter();
  }

  private refreshProductTableFilter(): void {
    const normalizedSearch = this.normalizeSearchText(this.productSearch);
    if (!this.showSelectedProductsOnly && !normalizedSearch) {
      this.items.filter = '';
      return;
    }

    this.items.filter = JSON.stringify({
      selectedOnly: this.showSelectedProductsOnly,
      selectedIds: this.selection.selected.map((product: any) => product.id).sort(),
      search: normalizedSearch,
    });
  }

  private syncAlwaysActiveSelectionToMode(): void {
    const selectedIds = new Set(
      this.pruneProductIdsToAvailableProducts(this.getSelectedAlwaysActiveIdsForMode(), this.getSelectedIdsForMode())
    );
    this.alwaysActiveSelection = new SelectionModel<any>(true, []);
    this.items.data.forEach((item: any) => {
      if (selectedIds.has(item.id)) {
        this.alwaysActiveSelection.select(item);
      }
    });
  }

  private syncRequiresApprovalSelectionToMode(): void {
    const selectedIds = new Set(
      this.pruneProductIdsToAvailableProducts(
        this.getSelectedRequiresApprovalIdsForMode(),
        this.getEligibleRequiresApprovalProductIds(this.getSelectedIdsForMode())
      )
    );
    this.requiresApprovalSelection = new SelectionModel<any>(true, []);
    this.items.data.forEach((item: any) => {
      if (selectedIds.has(item.id)) {
        this.requiresApprovalSelection.select(item);
      }
    });
  }

  private syncPayrollDeductionSelectionToMode(): void {
    const selectedProductIds = this.getSelectedIdsForMode();
    const disabledIds = new Set(
      this.pruneProductIdsToAvailableProducts(
        this.getPayrollDeductionDisabledProductIdsForMode(),
        selectedProductIds
      )
    );
    this.payrollDeductionSelection = new SelectionModel<any>(true, []);
    this.items.data.forEach((item: any) => {
      if (selectedProductIds.includes(item.id) && !disabledIds.has(item.id)) {
        this.payrollDeductionSelection.select(item);
      }
    });
  }

  private syncAllowanceControlSelectionToMode(): void {
    const selectedProductIds = this.getSelectedIdsForMode();
    const disabledIds = new Set(
      this.pruneProductIdsToAvailableProducts(
        this.getAllowanceControlDisabledProductIdsForMode(),
        selectedProductIds
      )
    );
    this.allowanceControlSelection = new SelectionModel<any>(true, []);
    this.items.data.forEach((item: any) => {
      if (selectedProductIds.includes(item.id) && !disabledIds.has(item.id)) {
        this.allowanceControlSelection.select(item);
      }
    });
  }

  private getSelectedIdsForMode(): string[] {
    if (this.mode === 'inherit') {
      return this.inheritedProductIds;
    }

    if (this.mode === 'none') {
      return [];
    }

    return this.customProductIds;
  }

  private getSelectedAlwaysActiveIdsForMode(): string[] {
    if (this.mode === 'inherit') {
      return this.inheritedAlwaysActiveProductIds;
    }

    if (this.mode === 'none') {
      return [];
    }

    return this.customAlwaysActiveProductIds;
  }

  private getSelectedRequiresApprovalIdsForMode(): string[] {
    if (this.mode === 'none') {
      return [];
    }

    if (this.mode === 'custom') {
      return this.customRequiresApprovalProductIds;
    }

    return this.pruneProductIdsToAvailableProducts(
      this.getRequiresApprovalProductIds(this.effectiveProductPayrollBenefitCategoriesMap),
      this.getEligibleRequiresApprovalProductIds(this.getSelectedIdsForMode())
    );
  }

  private getPayrollDeductionDisabledProductIdsForMode(): string[] {
    if (this.mode === 'inherit') {
      return this.inheritedPayrollDeductionDisabledProductIds;
    }

    if (this.mode === 'none') {
      return [];
    }

    return this.customPayrollDeductionDisabledProductIds;
  }

  private getAllowanceControlDisabledProductIdsForMode(): string[] {
    if (this.mode === 'inherit') {
      return this.inheritedAllowanceControlDisabledProductIds;
    }

    if (this.mode === 'none') {
      return [];
    }

    return this.customAllowanceControlDisabledProductIds;
  }

  private captureCustomSelectionFromTable(): void {
    if (!this.isCustomMode()) {
      return;
    }

    this.customProductIds = this.selection.selected.map((product: any) => product.id);
  }

  private captureCustomAlwaysActiveSelectionFromTable(): void {
    if (!this.isCustomMode()) {
      return;
    }

    this.customAlwaysActiveProductIds = this.pruneProductIdsToAvailableProducts(
      this.alwaysActiveSelection.selected.map((product: any) => product.id),
      this.getSelectedIdsForMode()
    );
  }

  private captureCustomRequiresApprovalSelectionFromTable(): void {
    if (!this.isCustomMode()) {
      return;
    }

    this.customRequiresApprovalProductIds = this.pruneProductIdsToAvailableProducts(
      this.requiresApprovalSelection.selected.map((product: any) => product.id),
      this.getEligibleRequiresApprovalProductIds(this.getSelectedIdsForMode())
    );
    const requiresApprovalProductIdSet = new Set(this.customRequiresApprovalProductIds);
    this.customProductPayrollBenefitCategoriesMap = Object.entries(this.customProductPayrollBenefitCategoriesMap || {})
      .reduce((result: Record<string, ProductBenefitMapping>, [productId, mapping]) => {
        const normalizedMapping = normalizePayrollBenefitMapping({
          ...(mapping || {}),
          requiresApproval: requiresApprovalProductIdSet.has(productId),
        });
        if (normalizedMapping) {
          result[productId] = normalizedMapping;
        }
        return result;
      }, {});
  }

  private captureCustomPayrollDeductionSelectionFromTable(): void {
    if (!this.isCustomMode()) {
      return;
    }

    const allowedProductIds = new Set(this.payrollDeductionSelection.selected.map((product: any) => product.id));
    this.customPayrollDeductionDisabledProductIds = this.getSelectedIdsForMode()
      .filter((productId) => !allowedProductIds.has(productId));
  }

  private captureCustomAllowanceControlSelectionFromTable(): void {
    if (!this.isCustomMode()) {
      return;
    }

    const enabledProductIds = new Set(this.allowanceControlSelection.selected.map((product: any) => product.id));
    this.customAllowanceControlDisabledProductIds = this.getSelectedIdsForMode()
      .filter((productId) => !enabledProductIds.has(productId));
  }

  private pruneProductIdsToAvailableProducts(productIds: string[], availableProductIds: string[]): string[] {
    const availableSet = new Set(availableProductIds || []);
    return (productIds || []).filter((productId) => availableSet.has(productId));
  }

  private getRequiresApprovalProductIds(
    productPayrollBenefitCategoriesMap: Record<string, ProductBenefitMapping>
  ): string[] {
    return Object.entries(productPayrollBenefitCategoriesMap || {})
      .filter(([, mapping]) => mapping?.requiresApproval === true && !!mapping?.category)
      .map(([productId]) => productId);
  }

  private getEligibleRequiresApprovalProductIds(productIds: string[]): string[] {
    return (productIds || []).filter((productId) => !!this.getProductPayrollBenefitMapping(productId)?.category);
  }

  private buildCustomProductPayrollBenefitCategoriesMap(
    selectedProductIds: string[],
    requiresApprovalProductIds: string[],
  ): Record<string, ProductBenefitMapping> {
    const selectedProductIdSet = new Set(selectedProductIds || []);
    const requiresApprovalProductIdSet = new Set(requiresApprovalProductIds || []);
    return Object.entries(this.customProductPayrollBenefitCategoriesMap || {})
      .reduce((result: Record<string, ProductBenefitMapping>, [productId, mapping]) => {
        if (!selectedProductIdSet.has(productId)) {
          return result;
        }

        const normalizedMapping = normalizePayrollBenefitMapping({
          ...(mapping || {}),
          requiresApproval: requiresApprovalProductIdSet.has(productId),
        });
        if (normalizedMapping) {
          result[productId] = normalizedMapping;
        }
        return result;
      }, {});
  }

  private pruneCustomProductPayrollBenefitCategoriesMapToSelectedProducts(): void {
    if (!this.isCustomMode()) {
      return;
    }

    const selectedProductIdSet = new Set(this.getSelectedIdsForMode());
    this.customProductPayrollBenefitCategoriesMap = Object.entries(this.customProductPayrollBenefitCategoriesMap || {})
      .reduce((result: Record<string, ProductBenefitMapping>, [productId, mapping]) => {
        if (selectedProductIdSet.has(productId)) {
          result[productId] = mapping;
        }
        return result;
      }, {});
    this.customRequiresApprovalProductIds = this.pruneProductIdsToAvailableProducts(
      this.customRequiresApprovalProductIds,
      this.getEligibleRequiresApprovalProductIds(this.getSelectedIdsForMode())
    );
    this.syncRequiresApprovalSelectionToMode();
  }

  private cloneProductPayrollBenefitCategoriesMap(
    map: Record<string, ProductBenefitMapping>
  ): Record<string, ProductBenefitMapping> {
    return JSON.parse(JSON.stringify(map || {}));
  }

  private clonePayrollBenefitSubcategoryDefinitionsByCategory(
    definitions: PayrollBenefitSubcategoryDefinitionsByCategory
  ): PayrollBenefitSubcategoryDefinitionsByCategory {
    return JSON.parse(JSON.stringify(definitions || {}));
  }

  private productMatchesFilter(product: any, filter: string): boolean {
    const productFilter = this.parseProductFilter(filter);
    const selectedIds = new Set(productFilter.selectedIds);
    const matchesSelectedOnly = !productFilter.selectedOnly || selectedIds.has(product?.id);
    const matchesSearch = !productFilter.search || this.getProductFilterText(product).includes(productFilter.search);

    return matchesSelectedOnly && matchesSearch;
  }

  private parseProductFilter(filter: string): { selectedOnly: boolean; selectedIds: string[]; search: string } {
    if (!filter) {
      return { selectedOnly: false, selectedIds: [], search: '' };
    }

    try {
      const parsed = JSON.parse(filter);
      return {
        selectedOnly: parsed?.selectedOnly === true,
        selectedIds: Array.isArray(parsed?.selectedIds) ? parsed.selectedIds : [],
        search: typeof parsed?.search === 'string' ? parsed.search : '',
      };
    } catch (_error) {
      return { selectedOnly: false, selectedIds: [], search: this.normalizeSearchText(filter) };
    }
  }

  private getProductFilterText(product: any): string {
    return this.normalizeSearchText([
      product?.vendorName,
      product?.productName,
      product?.id,
      product?.vendorSlug,
      product?.productSlug,
      [product?.vendorSlug, product?.productSlug].filter(value => !!value).join('-'),
    ].filter(value => !!value).join(' '));
  }

  private normalizeSearchText(value: string): string {
    return `${value || ''}`.trim().toLowerCase();
  }

}

export class CompanyProductsDialogModel {

  constructor(
    public products: any,
    public companyId: string = '',
    public companyName: string = '',
    public canInherit: boolean = false,
    public parentCompanyName: string = '',
    public initialMode: CompanyProductSettingMode = 'custom',
    public inheritedProducts: string[] = [],
    public alwaysActiveProducts: string[] = [],
    public inheritedAlwaysActiveProducts: string[] = [],
    public productPayrollBenefitCategoriesMap: Record<string, ProductBenefitMapping> = {},
    public payrollBenefitSubcategoryDefinitionsByCategory: PayrollBenefitSubcategoryDefinitionsByCategory = {},
    public payrollDeductionDisabledProducts: string[] = [],
    public inheritedPayrollDeductionDisabledProducts: string[] = [],
    public showPayrollDeductionColumn: boolean = false,
    public brandOptions: CompanyProductsBrandOption[] = [],
    public initialBrandId: string = '',
    public companyOptions: CompanyProductsCompanyOption[] = [],
    public allowanceControlDisabledProducts: string[] = [],
    public inheritedAllowanceControlDisabledProducts: string[] = [],
    public showAllowanceControlColumn: boolean = false,
  ) {
  }

}
